import { useId } from "react";
import { TriangleAlert } from "lucide-react";

import { CopyErrorDetailsButton } from "./CopyErrorDetailsButton";
import type { PlayerAIFailureNotice as FailureNoticeModel } from "./playerai.types";

/**
 * PB-5276 — a Player AI failure, presented as an outcome plus one next step.
 *
 * Three decisions are worth keeping when this is edited:
 *
 * - **`role="alert"`, not a status pill.** The card REPLACES whatever the
 *   reader was waiting on, and a reader who is not looking at the rail would
 *   otherwise wait on a response that already ended.
 * - **The recovery is `secondary`, never the accent.** Retry, Reconnect and
 *   Refresh only re-attempt what the reader already asked for; the design
 *   system reserves the accent for the control that COMMITS a region
 *   (`packages/ui/CLAUDE.md` §9), and a failure card commits nothing.
 * - **The technical detail is copied, never shown.** The card carries no code,
 *   no Run id and no stack; the copy control puts the host's pre-composed block
 *   on the clipboard so a reader can hand it to an administrator without the
 *   rail turning into a log viewer.
 */
export function PlayerAIFailureNotice({
  notice,
  onAction,
}: {
  readonly notice: FailureNoticeModel;
  readonly onAction?: (action: string) => void;
}) {
  const titleId = useId();
  const messageId = useId();

  return (
    <section
      role="alert"
      aria-labelledby={titleId}
      aria-describedby={messageId}
      className="w-full rounded-lg border border-pb-red/40 bg-pb-card/60 px-3 py-3 text-start"
    >
      <div className="flex items-start gap-2">
        <TriangleAlert
          aria-hidden="true"
          className="mt-0.5 h-4 w-4 shrink-0 text-pb-red-text"
        />
        <div className="min-w-0 flex-1 space-y-1">
          <p
            id={titleId}
            className="text-sm font-medium leading-snug text-pb-text"
          >
            {notice.title}
          </p>
          <p
            id={messageId}
            className="text-sm leading-snug text-pb-text-secondary"
          >
            {notice.message}
          </p>
        </div>
      </div>
      <div className="mt-2 flex flex-wrap items-center gap-1.5 ps-6">
        {notice.action ? (
          <button
            type="button"
            disabled={notice.action.busy === true}
            onClick={() => onAction?.(notice.action!.action)}
            className="inline-flex items-center rounded-full border border-pb-iris/40 bg-pb-card px-3 py-1.5 text-start text-xs font-medium text-pb-text transition-colors hover:border-pb-iris/70 hover:bg-pb-iris-soft/30 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {notice.action.label}
          </button>
        ) : null}
        <CopyErrorDetailsButton details={notice.details} />
      </div>
    </section>
  );
}
